import { Head, useForm } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { productEditorPath, productEditorSectionPath, productPath } from "@travily/routes";
import type ListingsEditorContent from "@travily/types/pages/ListingsEditorContent";
import { ButtonDock } from "@ui/components/ButtonDock";
import { StandardButton } from "@ui/components/StandardButton";
import { type ReactNode, useState } from "react";
import { useDirectUpload } from "@/hooks/use-direct-upload";
import { EditFrame } from "@/layouts/edit-frame";
import PersistentLayout from "@/layouts/persistent-layout";
import { buildEditorSections } from "@/lib/editor-sections";
import { firstError } from "@/lib/forms/first-error";
import { ProductContentEditor } from "./show.view";

type PageProps = ListingsEditorContent;

interface ContentFormData {
  delivery_kind: "file" | "link";
  delivery_url: string;
  // True once the seller clears the existing server-side file without picking a new one — tells the
  // save op to drop it rather than silently keep the prior attachment.
  remove_file: boolean;
  signed_id: string;
}

const ProductEditContentPage = (props: PageProps) => {
  const { listing_id, delivery_kind, delivery_url, delivery_file_name } = props;
  const sections = buildEditorSections(props);
  const initialKind = (delivery_kind ?? "file") as "file" | "link";

  const form = useForm<ContentFormData>({
    delivery_kind: initialKind,
    delivery_url: delivery_url ?? "",
    remove_file: false,
    signed_id: "",
  });

  const { upload, isUploading, progress } = useDirectUpload();
  const [picked, setPicked] = useState<{ name: string; size: number } | null>(null);

  const hasExistingFile = delivery_kind === "file" && delivery_file_name != null;
  const fileError = firstError(form.errors.signed_id) ?? firstError(form.errors.delivery_kind);
  const linkError = firstError(form.errors.delivery_url) ?? firstError(form.errors.delivery_kind);

  const handleFileSelect = async (file: File) => {
    setPicked({ name: file.name, size: file.size });
    form.setData("remove_file", false);
    const signedId = await upload(file);
    form.setData("signed_id", signedId);
  };

  const clearFile = () => {
    setPicked(null);
    form.setData("signed_id", "");
    form.setData("remove_file", true);
  };

  // Switching kind starts that mode fresh, so a file→link→file round-trip can't leave a phantom card
  // (picked name with no signed_id) or a stale remove_file blocking the still-attached server file.
  const setKind = (value: string) => {
    setPicked(null);
    if (value === "link") {
      form.setData({ ...form.data, delivery_kind: "link", remove_file: false, signed_id: "" });
    } else {
      form.setData({ ...form.data, delivery_kind: "file", delivery_url: "", remove_file: false });
    }
  };

  const handleSave = () => {
    form.patch(productEditorSectionPath(listing_id, "content"), {
      preserveScroll: "errors",
    });
  };

  const isSaveDisabled = (!form.isDirty && !form.data.signed_id) || form.processing || isUploading;

  return (
    <EditFrame
      activeSection="content"
      actions={
        <ButtonDock
          primaryAction={
            <>
              <StandardButton
                className="sm:hidden"
                color="green"
                isDisabled={isSaveDisabled}
                onPress={handleSave}
                size="56"
              >
                {t("common.actions.save")}
              </StandardButton>
              <StandardButton
                className="hidden sm:flex"
                color="green"
                isDisabled={isSaveDisabled}
                onPress={handleSave}
                size="64"
              >
                {t("common.actions.save")}
              </StandardButton>
            </>
          }
        />
      }
      backHref={productPath(listing_id)}
      href={productEditorPath(listing_id)}
      sections={sections}
      title={t("web.products.editors.show.title")}
    >
      <Head title={t("web.products.editor.sections.show.meta_title", { title: props.listing_title ?? "" })} />

      <ProductContentEditor
        deliveryKind={form.data.delivery_kind}
        deliveryUrl={form.data.delivery_url}
        fileError={fileError}
        fileName={
          picked?.name ?? (hasExistingFile && !form.data.remove_file ? (delivery_file_name ?? undefined) : undefined)
        }
        fileSize={picked?.size}
        isUploading={isUploading}
        linkError={linkError}
        onDeliveryUrlChange={(value) => form.setData("delivery_url", value)}
        onFileRemove={clearFile}
        onFileSelect={handleFileSelect}
        onKindChange={setKind}
        progress={progress}
      />
    </EditFrame>
  );
};

ProductEditContentPage.layout = (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

export default ProductEditContentPage;
